import { AfterViewInit, Component, Injector, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { AjastettuTyo, AjastettuTyoService, AjastetunTyonTyyppi } from '@lemon/core-firebase/eraajot/ajastettu/ajastetut-tyot.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { AjastetutTyotDataDialog } from 'app/yllapito/ajastetut-tyot/ajastetut-tyot-data.dialog'
import { combineLatest, map, Subject, switchMap, takeUntil, tap } from 'rxjs'

interface AjastetutTyotLomake {
  tyyppi: string | null
  tulossaVaiHistoria: 'tulossa' | 'historia'
}
@Component({
  templateUrl: './ajastetut-tyot.component.html',
  styleUrls: ['./ajastetut-tyot.component.css'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AjastetutTyotComponent implements OnInit, OnDestroy, AfterViewInit {
  naytettavatKolumnit: string[] = ['ajoaika', 'selite']

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  hakulomake: FieldTree<AjastetutTyotLomake> = form(signal<AjastetutTyotLomake>({ tyyppi: null, tulossaVaiHistoria: 'tulossa' }))

  @ViewChild(MatSort, { static: true }) _sort: MatSort
  @ViewChild(MatPaginator, { static: true }) _paginator: MatPaginator

  dataSource = new MatTableDataSource<{ data: AjastettuTyo<any>[] }>([])
  private _kirjanpitajaOnDevaaja: boolean

  ajastetunTyonTyyppi: { tyyppi: AjastetunTyonTyyppi; teksti: string }[] = []

  readonly asiakas = signal<Asiakas>(undefined)

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _asiakasService: AsiakasService,
    private _ajastettuTyoService: AjastettuTyoService,
    private _dialog: MatDialog,
  ) {}

  ngOnInit() {
    this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(onkoDevaaja => {
      this._kirjanpitajaOnDevaaja = onkoDevaaja
    })

    this.ajastetunTyonTyyppi = this._ajastettuTyoService.getAjastetunTyonTyypit()

    const tyyppiKentta = this.hakulomake.tyyppi
    const tulossaVaiHistoriaKentta = this.hakulomake.tulossaVaiHistoria

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas.set(asiakas)
    })

    combineLatest([
      arvonMuutokset(tyyppiKentta, { injector: this._injector }),
      this._asiakasService.nykyinenAsiakasAvainObservable,
      arvonMuutokset(tulossaVaiHistoriaKentta, { injector: this._injector }),
    ])
      .pipe(
        tap(() => this._ladataanService.aloitaLataaminen()),
        switchMap(([tyyppi, asiakas, tulossaVaiHistoria]) => {
          if (!asiakas || !tyyppi || !tulossaVaiHistoria) {
            return []
          }

          const collection = tulossaVaiHistoria === 'tulossa' ? this._ajastettuTyoService.collectionName : this._ajastettuTyoService.collectionNameHistory
          return this._firebaseLemonator
            .firestoreCollection<AjastettuTyo<any>>(collection)
            .orderBy('ajoaika', 'desc')
            .limit(1000)
            .where('asiakasAvain', '==', asiakas.avain)
            .listen()
            .pipe(
              map(tyot => {
                // Overwrite selite field with standard explanation
                for (const tyo of tyot) {
                  tyo.selite = this.ajastetunTyonTyyppi.find(t => t.tyyppi === tyo.tyyppi)?.teksti ?? tyo.selite
                }
                if (tyyppi && tyyppi !== 'all') {
                  return tyot.filter(tyo => tyo.tyyppi === tyyppi)
                }
                return tyot
              }),
            )
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tyot => {
        this.dataSource.data = tyot
        this.dataSource.paginator = this._paginator
        this._ladataanService.lopetaLataaminen()
      })

    asetaArvo(tyyppiKentta, 'all')
    asetaArvo(tulossaVaiHistoriaKentta, 'tulossa')
  }

  ngAfterViewInit() {
    this.dataSource.sort = this._sort
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  katsoTiedot(rivi: AjastettuTyo<any>) {
    if (this._kirjanpitajaOnDevaaja) {
      this._dialog.open(AjastetutTyotDataDialog, { data: rivi })
    }
  }
}
